// E8PowerProductCombineClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 4) – mnozenie poteg o tych samych podstawach:
// b1^p * b2 * b2^q * b1^r = b1^(p+r) * b2^(q+1).

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const BASE_PAIRS = [
  [2, 3], [3, 2], [2, 5], [5, 2], [3, 5], [5, 3],
];

function randInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function combined(b1, e1, b2, e2) {
  return `${b1}^{${e1}}\\cdot ${b2}^{${e2}}`;
}

function buildData({ base1, base2, p, q, r, fixedOptions = null }) {
  const questionLatex = `${base1}^{${p}}\\cdot ${base2}\\cdot ${base2}^{${q}}\\cdot ${base1}^{${r}}`;
  const correct = combined(base1, p + r, base2, q + 1);

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((latex, index) => ({ latex, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(correct);
  } else {
    const distractors = [
      combined(base1 * base1, p + r, base2 * base2, q + 1), // bledne podwojenie podstaw
      combined(base1, p * r, base2, q),                     // mnozenie wykladnikow / pominiecie samotnej podstawy
      combined(base1 * base1, p * r, base2 * base2, q),     // oba bledy
    ];
    const ordered = shuffle([correct, ...distractors]);
    options = ordered.map((latex, index) => ({ latex, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(correct);
  }

  return { base1, base2, p, q, r, questionLatex, correct, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8PowerProductCombineClosedTask",
    data: buildData({
      base1: 2,
      base2: 3,
      p: 5,
      q: 4,
      r: 3,
      fixedOptions: [
        "2^{8}\\cdot 3^{5}",
        "4^{8}\\cdot 9^{5}",
        "2^{15}\\cdot 3^{4}",
        "4^{15}\\cdot 9^{4}",
      ],
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 4,
    },
    tags: ["e8", "potegi", "mnozenie-poteg"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const [base1, base2] = BASE_PAIRS[Math.floor(Math.random() * BASE_PAIRS.length)];
  let p;
  let r;
  do {
    p = randInt(2, 5);
    r = randInt(2, 5);
  } while (p + r === p * r); // unikamy p=r=2 (kolizja wykladnikow)
  const q = randInt(2, 4);

  return {
    type: "E8PowerProductCombineClosedTask",
    data: buildData({ base1, base2, p, q, r }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_4" },
    tags: ["e8", "potegi", "mnozenie-poteg"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Wartość wyrażenia <InlineMath math={data.questionLatex} /> jest równa
        wartości wyrażenia
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.latex}>
            <ChoiceOptionMath letter={LETTERS[index]} math={option.latex} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={data.correct} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={option.latex} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const { base1, base2, p, q, r } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Grupujemy potęgi o tej samej podstawie (pamiętając,
        że <InlineMath math={`${base2}=${base2}^{1}`} />).
      </p>
      <BlockMath
        math={`${data.questionLatex}=${base1}^{${p}}\\cdot ${base1}^{${r}}\\cdot ${base2}^{1}\\cdot ${base2}^{${q}}`}
      />
      <p>
        <strong>Krok 2.</strong> Mnożąc potęgi o tej samej podstawie, dodajemy
        wykładniki.
      </p>
      <BlockMath
        math={`=${base1}^{${p}+${r}}\\cdot ${base2}^{1+${q}}=${data.correct}`}
      />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
